import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Lock, Loader2, ArrowLeft, CheckCircle2, AlertTriangle, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import useSeo from "../hooks/useSeo";

export default function ResetPasswordPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useSeo({
    title: "Restablecer contraseña · Rivones",
    description: "Establece una nueva contraseña para tu cuenta de Rivones.",
    canonical: "https://rivones.com/reset-password",
  });

  useEffect(() => {
    if (!token) {
      toast.error("Enlace inválido");
    }
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 6) {
      toast.error("La contraseña debe tener al menos 6 caracteres");
      return;
    }
    if (password !== confirm) {
      toast.error("Las contraseñas no coinciden");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/auth/password-reset/confirm", {
        token,
        new_password: password,
      });
      setDone(true);
      toast.success("¡Contraseña actualizada!");
      setTimeout(() => navigate("/login", { replace: true }), 2500);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(
        typeof detail === "string"
          ? detail
          : "No pudimos restablecer tu contraseña. Solicita un nuevo enlace.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-sand flex items-center justify-center p-4">
        <div className="w-full max-w-md bg-white border border-neutral-200 rounded-3xl p-8 text-center">
          <div className="inline-flex w-14 h-14 rounded-2xl bg-red-50 items-center justify-center mb-4">
            <AlertTriangle className="w-7 h-7 text-red-600" />
          </div>
          <h1 className="font-heading font-bold text-xl text-[#111111] mb-2">Enlace inválido</h1>
          <p className="text-neutral-600 text-sm">
            El enlace no es válido o falta el token. Solicita uno nuevo desde la página de recuperación.
          </p>
          <Link
            to="/forgot-password"
            className="inline-flex items-center justify-center mt-6 px-5 py-2.5 rounded-full bg-[#111111] text-white text-sm font-medium hover:bg-neutral-800"
            data-testid="reset-invalid-cta"
          >
            Solicitar enlace
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen bg-sand flex items-center justify-center p-4 sm:p-8"
      data-testid="reset-password-page"
    >
      <div className="w-full max-w-md">
        <Link
          to="/login"
          className="inline-flex items-center gap-1.5 text-sm text-neutral-500 hover:text-[#111111] mb-6 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Volver al login
        </Link>

        <div className="bg-white border border-neutral-200 rounded-3xl p-7 sm:p-9 shadow-sm">
          {done ? (
            <div className="text-center" data-testid="reset-success-state">
              <div className="inline-flex w-14 h-14 rounded-2xl bg-emerald-50 items-center justify-center mb-4">
                <CheckCircle2 className="w-7 h-7 text-emerald-600" />
              </div>
              <h1 className="font-heading font-bold text-2xl text-[#111111] mb-2">
                ¡Listo!
              </h1>
              <p className="text-neutral-600 text-sm">
                Tu contraseña fue actualizada. Te llevamos al login…
              </p>
            </div>
          ) : (
            <>
              <div className="inline-flex w-12 h-12 rounded-2xl bg-terracotta/10 items-center justify-center mb-4">
                <Lock className="w-6 h-6 text-terracotta" />
              </div>
              <h1 className="font-heading font-bold text-2xl sm:text-3xl text-[#111111] mb-2">
                Nueva contraseña
              </h1>
              <p className="text-neutral-600 text-sm mb-6 leading-relaxed">
                Crea una contraseña segura de al menos 6 caracteres. Después de actualizarla
                cerraremos todas tus sesiones activas por seguridad.
              </p>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="new-password" className="text-sm font-medium text-[#111111]">
                    Nueva contraseña
                  </Label>
                  <div className="relative mt-1.5">
                    <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
                    <Input
                      id="new-password"
                      type={show ? "text" : "password"}
                      autoComplete="new-password"
                      placeholder="••••••••"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="pl-9 pr-10 h-11"
                      required
                      minLength={6}
                      data-testid="reset-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShow((v) => !v)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-700"
                      aria-label={show ? "Ocultar" : "Mostrar"}
                    >
                      {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>

                <div>
                  <Label htmlFor="confirm-password" className="text-sm font-medium text-[#111111]">
                    Confirma la contraseña
                  </Label>
                  <div className="relative mt-1.5">
                    <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
                    <Input
                      id="confirm-password"
                      type={show ? "text" : "password"}
                      autoComplete="new-password"
                      placeholder="••••••••"
                      value={confirm}
                      onChange={(e) => setConfirm(e.target.value)}
                      className="pl-9 h-11"
                      required
                      minLength={6}
                      data-testid="reset-confirm-input"
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={submitting}
                  className="w-full h-11 bg-[#111111] hover:bg-neutral-800 text-white rounded-full font-medium"
                  data-testid="reset-submit-btn"
                >
                  {submitting ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    "Actualizar contraseña"
                  )}
                </Button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
